import type { Locale } from '@worldcoo/monolith/core';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { isServer } from '@tanstack/react-query';

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

// Cache for Intl.NumberFormat instances to avoid recreating them
const numberFormatCache = new Map<string, Intl.NumberFormat>();

function getNumberFormatter(locale: string, decimalDigits: number): Intl.NumberFormat {
	// Normalize locale for SSR consistency
	const normalizedLocale = locale === 'eu' || locale === 'gl' || locale === 'it' ? 'es' : locale;

	const cacheKey = `${normalizedLocale}-${decimalDigits}`;

	let formatter = numberFormatCache.get(cacheKey);
	if (!formatter) {
		formatter = new Intl.NumberFormat(normalizedLocale, {
			style: 'decimal',
			minimumFractionDigits: decimalDigits,
			maximumFractionDigits: decimalDigits,
		});
		numberFormatCache.set(cacheKey, formatter);
	}

	return formatter;
}

/**
 * Format a number with locale-aware separators.
 * @param decimalDigits - exact number of decimal places (default: 2)
 */
export function formatNumber(locale: string, number: number, decimalDigits = 2) {
	// Fix SSR mismatch error where numbers in Euskera or Galician are formatted in chrome with commas but in node are dots
	return getNumberFormatter(locale, decimalDigits).format(number);
}

/**
 * Format a large number as a compact string like `+101M` or `+432K`.
 * Falls back to locale-formatted number for values < 1000.
 * @param suffix - optional unit appended after a space (e.g. `€` → `+7M €`)
 * @param decimalDigits - fraction digits for the compact value (default: 0 → `+7M`, 1 → `+1.1M`)
 */
export function formatCompactNumber(
	locale: string,
	number: number,
	options?: { suffix?: string; decimalDigits?: number },
) {
	const suffix = options?.suffix ? ` ${options.suffix}` : '';
	const digits = options?.decimalDigits ?? 0;

	if (number >= 1_000_000) {
		const value = digits === 0 ? Math.floor(number / 1_000_000) : (number / 1_000_000).toFixed(digits);
		return `+${value}M${suffix}`;
	}
	if (number >= 1_000) {
		const value = digits === 0 ? Math.floor(number / 1_000) : (number / 1_000).toFixed(digits);
		return `+${value}K${suffix}`;
	}

	return `${formatNumber(locale, number, 0)}${suffix}`;
}

// Cache for Intl.ListFormat instances to avoid recreating them
const listFormatCache = new Map<string, Intl.ListFormat>();

function getListFormatter(locale: Locale): Intl.ListFormat {
	// Normalize locale for SSR consistency
	const normalizedLocale = locale === 'eu' || locale === 'gl' || locale === 'it' ? 'es' : locale;

	let formatter = listFormatCache.get(normalizedLocale);
	if (!formatter) {
		formatter = new Intl.ListFormat(normalizedLocale, {
			style: 'long',
			type: 'conjunction',
		});
		listFormatCache.set(normalizedLocale, formatter);
	}

	return formatter;
}

export const formatList = (locale: Locale, items: string[]) => {
	// Fix SSR mismatch error where numbers in Euskera or Galician are formatted in chrome with commas but in node are dots
	return getListFormatter(locale).format(items);
};

export const savedTaxAmount = (amount: number) => {
	// 80% tax deduction for the first 250€
	// 40% tax deduction for the amount above 250€
	// Only for natural persons resident in Spain
	// Based on Real Decreto-ley 6/2023 de 19 de diciembre: https://www.boe.es/buscar/act.php?id=BOE-A-2023-25758&p=20250103&tn=1#a1-41
	return Math.min(amount, 250) * 0.8 + (amount - Math.min(amount, 250)) * 0.4;
};

/** Format date as local date, ideal for inputs */
export const formatDate = (dateString: string | undefined) => {
	if (!dateString) return;

	const date = new Date(dateString);

	const year = date.getFullYear().toString().padStart(2, '0');
	const month = (date.getMonth() + 1).toString().padStart(2, '0');
	const day = date.getDate().toString().padStart(2, '0');

	return `${year}-${month}-${day}`;
};

/** Format date as local datetime, ideal for inputs */
export const formatDatetime = (dateString: string | undefined) => {
	if (!dateString) return;

	const date = new Date(dateString);

	const year = date.getFullYear().toString().padStart(2, '0');
	const month = (date.getMonth() + 1).toString().padStart(2, '0');
	const day = date.getDate().toString().padStart(2, '0');
	const hours = date.getHours().toString().padStart(2, '0');
	const minutes = date.getMinutes().toString().padStart(2, '0');

	return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export function preloadImageOnClient(src: string | undefined) {
	if (isServer) return;
	if (!src) return;
	const img = new Image();
	img.fetchPriority = 'low';
	img.src = src;
}

export function calculateDaysLeft(date: string) {
	return Math.ceil((new Date(date).getTime() - Date.now()) / (1000 * 60 * 60 * 24)).toString();
}

export function calculateExpiryDate(days: number) {
	return new Date(Date.now() + days * 24 * 60 * 60 * 1000);
}

export const heroImageSize = { width: 630, height: 433, className: 'lg:h-[433px] aspect-630/433' };
export const campaignImageSize = { width: 381, height: 250, className: 'h-[250px] aspect-381/250' };
